<template>
  <div class="dashboard-container">
    <!-- Loader: Top Progress Bar -->
    <div
      v-if="loading"
      class="progress-bar"
      :style="{ width: progress + '%' }"
    ></div>

    <div class="container">
      <!-- Form Title -->
      <div class="mb-2 page-title">
        <i class="bi bi-file-earmark-text"></i>
        {{ isEdit ? "Ubah Turnamen" : "Tambah Turnamen" }}
      </div>

      <!-- Pesan jika gagal memuat karena jaringan lemah -->
      <div v-if="fetchFailed" class="alert alert-warning d-flex align-items-center justify-content-between my-3 py-2 px-3">
        <div>
          <i class="bi bi-wifi-off me-2"></i>
          Gagal memuat data turnamen (koneksi lambat/terputus).
        </div>
        <button type="button" class="btn btn-sm btn-outline-dark" @click="fetchTournamentDetail(targetId)">
          <i class="bi bi-arrow-clockwise me-1"></i> Coba Lagi
        </button>
      </div>

      <!-- Form -->
      <form novalidate @submit.prevent="submitForm" class="admin-form mt-4">
        <div class="row">
          <!-- Tournament Name -->
          <div class="col-lg-6">
            <div class="mb-3">
              <label for="name" class="form-label">Nama Turnamen</label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.name }"
                id="name"
                v-model="form.name"
                @input="hapusError('name')"
              />
              <div class="invalid-feedback">{{ formatError(errors.name) }}</div>
            </div>

            <div class="mb-3">
              <label for="document" class="form-label">Proposal Kejuaraan (PDF)</label>
              <div class="input-group">
                <input class="form-control" type="file" id="document" @change="handleFileUpload($event, 'document')">
              </div>
              <div class="invalid-feedback">{{ formatError(errors.tournament_document || errors.document) }}</div>
              
            </div> 

            <div class="mb-3" v-if="isEdit">
              <a v-if="form.existingDocumentUrl" :href="form.existingDocumentUrl" target="_blank" rel="noopener" class="button button-primary">
                <i class="bi bi-file-earmark-post-fill"></i> Lihat Berkas
              </a>

            </div>

            <div class="mb-3">
              <label for="image" class="form-label">Banner / Poster Turnamen</label>
              <div class="input-group">
                <input class="form-control" type="file" id="image" @change="handleFileUpload($event, 'image')">
              </div>
              <div class="invalid-feedback">{{ formatError(errors.tournament_image || errors.image) }}</div>
            </div> 

            <div class="mb-3" v-if="isEdit && form.existingImageUrl">
              <a :href="form.existingImageUrl" target="_blank" rel="noopener" class="button button-primary">
                <i class="bi bi-file-earmark-post-fill"></i> Lihat Banner
              </a>
            </div>

            <!-- Logo Event / Kejuaraan (Multi-Tenant) -->
            <div class="mb-3">
              <label for="logo" class="form-label d-flex align-items-center justify-content-between">
                <span>Logo Resmi Event (Multi-Tenant)</span>
                <span class="badge bg-primary-subtle text-primary border border-primary-subtle small">Invoice, Kwitansi & Web</span>
              </label>
              <div class="input-group">
                <input class="form-control" type="file" id="logo" accept="image/png,image/jpeg,image/webp,image/svg+xml" @change="handleFileUpload($event, 'logo')">
              </div>
              <div class="form-text">
                Format PNG (disarankan transparan), JPG, atau SVG (maks 5MB). Logo ini akan menggantikan logo standar pada invoice, kwitansi, serta header saat kontingen mengakses event ini.
              </div>
              <div class="invalid-feedback">{{ formatError(errors.tournament_logo || errors.logo) }}</div>
            </div>

            <div class="mb-3 d-flex align-items-center gap-2" v-if="previewLogoUrl || form.existingLogoUrl">
              <img :src="previewLogoUrl || form.existingLogoUrl" alt="Preview Logo Event" style="width: 50px; height: 50px; object-fit: contain; background: #f8fafc; border: 1px solid #cbd5e1; border-radius: 6px; padding: 4px;" />
              <span class="small text-muted">{{ previewLogoUrl ? 'Logo baru dipilih' : 'Logo event saat ini' }}</span>
            </div>

            <div class="mb-3">
              <label for="technical_meeting_date" class="form-label">Tanggal Rapat Teknis</label>
              <input
                type="date"
                class="form-control"
                :class="{ 'is-invalid': errors.technical_meeting_date }"
                id="technical_meeting_date"
                v-model="form.technical_meeting_date"
                @change="hapusError('technical_meeting_date')"
                @input="hapusError('technical_meeting_date')"
              />
              <div class="invalid-feedback">{{ formatError(errors.technical_meeting_date) }}</div>
            </div>

            <div class="mb-3">
              <label for="registration_deadline" class="form-label">Tanggal Terakhir Pendaftaran</label>
              <input
                type="date"
                class="form-control"
                :class="{ 'is-invalid': errors.registration_deadline }"
                id="registration_deadline"
                v-model="form.registration_deadline"
                @change="hapusError('registration_deadline')"
                @input="hapusError('registration_deadline')"
              />
              <div class="form-text">
                Ditampilkan sebagai hitung mundur di kartu acara. Kosongkan bila belum ditentukan.
              </div>
              <div class="invalid-feedback">{{ formatError(errors.registration_deadline) }}</div>
            </div>

            <div class="mb-3">
              <label for="kuota_atlet" class="form-label">Batas Jumlah Atlet (Kuota Event)</label>
              <input
                type="number" min="1" max="100000"
                class="form-control"
                :class="{ 'is-invalid': errors.kuota_atlet }"
                id="kuota_atlet"
                v-model="form.kuota_atlet"
                placeholder="Kosongkan bila tidak dibatasi, mis. 3000"
                @input="hapusError('kuota_atlet')"
              />
              <div class="form-text">
                <template v-if="isEdit">Terdaftar saat ini: <strong>{{ jumlahAtlet }}</strong> atlet<span v-if="form.kuota_atlet"> dari {{ form.kuota_atlet }}</span>. </template>
                Begitu penuh, pendaftaran otomatis tertutup dan kartu acara berlabel "Kuota Penuh".
              </div>
              <div class="invalid-feedback">{{ formatError(errors.kuota_atlet) }}</div>
            </div>

            <div class="mb-3">
              <label for="whatsapp_group" class="form-label">Tautan Grup WhatsApp</label>
              <input
                type="url"
                class="form-control"
                :class="{ 'is-invalid': errors.whatsapp_group }"
                id="whatsapp_group"
                v-model="form.whatsapp_group"
                @input="hapusError('whatsapp_group')"
                placeholder="https://chat.whatsapp.com/XXXXXXXXXXXX"
              />
              <div class="form-text">
                Tautan undangan grup, bukan nomor telepon.
                WhatsApp &rarr; grup &rarr; Undang lewat tautan &rarr; Salin tautan.
              </div>
              <div class="invalid-feedback">{{ formatError(errors.whatsapp_group) }}</div>
            </div>

            <div class="mb-3">
              <label for="start_date" class="form-label">Tanggal Mulai</label>
              <input
                type="date"
                class="form-control"
                :class="{ 'is-invalid': errors.start_date }"
                id="start_date"
                v-model="form.start_date"
                @change="hapusError('start_date')"
                @input="hapusError('start_date')"
              />
              <div class="invalid-feedback">{{ formatError(errors.start_date) }}</div>
            </div>

            <div class="mb-3">
              <label for="end_date" class="form-label">Tanggal Selesai</label>
              <input
                type="date"
                class="form-control"
                :class="{ 'is-invalid': errors.end_date }"
                id="end_date"
                v-model="form.end_date"
                @change="hapusError('end_date')"
                @input="hapusError('end_date')"
              />
              <div class="invalid-feedback">{{ formatError(errors.end_date) }}</div>
            </div>

            
          </div>
          <!-- Person Responsible -->
          <div class="col-lg-6">
            <!--
              Status pendaftaran.
              Pilihan lama hanya "active"/"inactive", sehingga tidak ada cara
              membedakan turnamen yang BELUM dibuka dari yang SUDAH selesai —
              keduanya sama-sama "inactive" dan sama-sama masih muncul di
              halaman utama. Empat status di bawah memisahkan ketiganya, plus
              satu status untuk benar-benar menyembunyikan turnamen.
            -->
            <div class="mb-3">
              <label for="status" class="form-label">Status Pendaftaran</label>

              <select
                class="form-select"
                id="status"
                name="status"
                v-model="form.status"
                :class="{ 'is-invalid': errors.status }"
                aria-describedby="status-bantuan"
              >
                <option value="dibuka">Dibuka — kontingen bisa mendaftar</option>
                <option value="segera_hadir">Segera hadir — tampil, tanpa tombol daftar</option>
                <option value="ditutup">Pendaftaran ditutup — tampil, tidak bisa mendaftar</option>
                <option value="selesai">Selesai — tampil, tanpa tombol daftar</option>
                <option value="sembunyikan">Sembunyikan — hilang dari halaman publik</option>
              </select>
              <div class="invalid-feedback">{{ errors.status }}</div>

              <small id="status-bantuan" class="form-text d-block mt-2">
                <i class="bi bi-info-circle"></i>
                {{ penjelasanStatus }}
              </small>
            </div>

            <div class="mb-3">
              <label for="description" class="form-label">Keterangan</label>
              <textarea
                class="form-control"
                :class="{ 'is-invalid': errors.description }"
                id="description"
                v-model="form.description"
                @input="hapusError('description')"
              ></textarea>
              <div class="invalid-feedback">{{ formatError(errors.description) }}</div>
            </div>

            <div class="mb-3">
              <label for="location" class="form-label">Lokasi</label>
              <textarea
                class="form-control"
                :class="{ 'is-invalid': errors.location }"
                id="location"
                v-model="form.location"
                @input="hapusError('location')"
              ></textarea>
              <div class="invalid-feedback">{{ formatError(errors.location) }}</div>
            </div>

            <div class="mb-3">
              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="tampilkan_kuota_kelas"
                  v-model="form.tampilkan_kuota_kelas"
                />
                <label class="form-check-label fw-bold" for="tampilkan_kuota_kelas">
                  Tampilkan Menu Ketersediaan Kelas ke Official
                </label>
              </div>
              <div class="form-text">
                Jika diaktifkan, official dapat membuka menu/halaman Ketersediaan Kelas. Jika dinonaktifkan, menu disembunyikan namun sistem tetap otomatis membatasi pilihan jika kelas penuh saat mendaftarkan atlet.
              </div>
            </div>
          </div>
        </div>

        <!--
          REKENING PEMBAYARAN.

          Kolomnya sudah ada di basis data dan sudah divalidasi server sejak
          lama, tapi tidak pernah punya isian di formulir ini — jadi tidak ada
          cara mengisinya, dan semua acara berjalan dengan rekening kosong.

          Akibatnya tagihan terbit dengan nomor rekening "-", dan pelatih yang
          hendak mentransfer tidak punya tujuan. Nomornya disalin ke setiap
          tagihan saat diterbitkan, jadi mengganti rekening bulan depan tidak
          mengubah tagihan yang sudah dibayar.
        -->
        <div class="row">
          <div class="col-lg-12">
            <h3 class="rekening-judul">
              <i class="bi bi-bank"></i> Rekening Pembayaran
            </h3>
            <p class="rekening-catatan">
              <i class="bi bi-exclamation-triangle-fill"></i>
              Ketik nomor rekeningnya sendiri dan <strong>periksa ulang angkanya
              satu per satu</strong> sebelum menyimpan. Nomor ini yang dipakai
              seluruh kontingen untuk mentransfer; satu angka keliru berarti
              uang masuk ke rekening orang lain, dan baru ketahuan setelah ada
              yang mengeluh.
            </p>
          </div>

          <div class="col-lg-4">
            <div class="mb-3">
              <label for="bank_name" class="form-label">Nama Bank</label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.bank_name }"
                id="bank_name"
                placeholder="mis. BRI, BCA, Mandiri"
                v-model="form.bank_name"
                @input="hapusError('bank_name')"
              />
              <div class="invalid-feedback">{{ formatError(errors.bank_name) }}</div>
            </div>
          </div>

          <div class="col-lg-4">
            <div class="mb-3">
              <label for="bank_account_number" class="form-label">Nomor Rekening</label>
              <input
                type="text"
                inputmode="numeric"
                class="form-control"
                :class="{ 'is-invalid': errors.bank_account_number }"
                id="bank_account_number"
                autocomplete="off"
                v-model="form.bank_account_number"
                @input="hapusError('bank_account_number')"
              />
              <div class="invalid-feedback">{{ formatError(errors.bank_account_number) }}</div>
            </div>
          </div>

          <div class="col-lg-4">
            <div class="mb-3">
              <label for="bank_account_name" class="form-label">Atas Nama</label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.bank_account_name }"
                id="bank_account_name"
                placeholder="mis. Cenderawasih Juara Manajemen"
                v-model="form.bank_account_name"
                @input="hapusError('bank_account_name')"
              />
              <div class="invalid-feedback">{{ formatError(errors.bank_account_name) }}</div>
            </div>
          </div>

          <div class="col-lg-12">
            <div class="mb-3">
              <label for="payment_note" class="form-label">Catatan Pembayaran (opsional)</label>
              <textarea
                class="form-control"
                id="payment_note"
                rows="2"
                placeholder="mis. Cantumkan nama kontingen pada berita transfer."
                v-model="form.payment_note"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="row">
          <div class="col-lg-12 text-center">
            <button type="submit" class="button button-primary" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              <i v-else class="bi bi-floppy me-1"></i>
              <span>{{ loading ? "Sedang Mengunggah & Menyimpan..." : (isEdit ? "Update Tournament" : "Tambah Turnamen") }}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import http from "@/services/http";
import { useToast } from "vue-toastification";
import { normalkanStatus } from "@/utils/status";

function keYmd(val) {
  if (!val) return '';
  const str = String(val).trim();
  const cocokYmd = str.match(/^(\d{4}-\d{2}-\d{2})/);
  if (cocokYmd) return cocokYmd[1];
  const d = new Date(str);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default {
  name: "TournamentForm",
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
    id: {
      type: [String, Number],
      default: null,
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      jumlahAtlet: 0,
      TournamentId: null,
      form: {
        name: "",
        tournament_document: null,
        tournament_image: null,
        tournament_logo: null,
        existingDocumentUrl : null,
        existingImageUrl : null,
        existingLogoUrl : null,
        technical_meeting_date: "",
        registration_deadline: "",
        kuota_atlet: "",
        whatsapp_group: "",
        start_date: "",
        end_date: "",
        status: "dibuka",
        description: "",
        location: "",
        bank_name: "",
        bank_account_number: "",
        bank_account_name: "",
        payment_note: "",
        tampilkan_kuota_kelas: true,
      },
      previewLogoUrl: null,
      errors: {},
      loading: false,
      fetchFailed: false,
      progress: 0,
    };
  },

  computed: {
    targetId() {
      return this.id || this.TournamentId || this.$route.params.id;
    },

    /**
     * Menjelaskan akibat status yang sedang dipilih.
     * Istilah "sembunyikan" dan "selesai" mudah tertukar; keterangan langsung
     * di bawah pilihan mencegah panitia tidak sengaja melenyapkan turnamen
     * yang sebenarnya hanya ingin ditandai sudah usai.
     */
    penjelasanStatus() {
      return {
        dibuka: 'Turnamen tampil di halaman utama dan tombol "Daftar" aktif.',
        segera_hadir: 'Turnamen tampil sebagai pengumuman, tetapi tombol "Daftar" belum muncul.',
        selesai: 'Turnamen tetap tampil beserta jadwal dan hasilnya, tanpa tombol "Daftar".',
        sembunyikan: 'Turnamen hilang dari halaman utama, daftar turnamen, dan halaman peserta. Datanya tidak dihapus.',
      }[this.form.status] || 'Pilih salah satu status di atas.';
    },
  },

  created() {
    this.TournamentId = this.id || this.$route.params.id;
    
    if (this.isEdit && this.targetId) {
      this.fetchTournamentDetail(this.targetId);
    }
  },

  watch: {
    targetId(newId) {
      if (this.isEdit && newId) {
        this.fetchTournamentDetail(newId); 
      }
    }
  },

  methods: {

    formatError(err) {
      if (!err) return "";
      if (Array.isArray(err)) return err[0] || "";
      return String(err);
    },

    hapusError(field) {
      if (this.errors && this.errors[field]) {
        delete this.errors[field];
      }
    },

    handleFileUpload(event, explicitField) {
      const field = explicitField || event.target.id;
      const file = event.target.files[0];

      if (!file) return;

      if (field === 'document') {
        this.form.tournament_document = file;
        this.hapusError('tournament_document');
        this.hapusError('document');
      } else if (field === 'image') {
        this.form.tournament_image = file;
        this.hapusError('tournament_image');
        this.hapusError('image');
      } else if (field === 'logo') {
        this.form.tournament_logo = file;
        this.previewLogoUrl = URL.createObjectURL(file);
        this.hapusError('tournament_logo');
        this.hapusError('logo');
      }
    },

    
    async fetchTournamentDetail(id) {
      const fetchId = id || this.targetId;
      if (!fetchId) return;
      this.loading = true;
      this.fetchFailed = false;
      try {
        const response = await http.get(`/tournament-settings/${fetchId}`);
        if (response.data) {
          const d = response.data;
          this.jumlahAtlet = d.jumlah_atlet ?? 0;
          this.form = {
            name: d.name || "",
            existingDocumentUrl: d.document,
            existingImageUrl: d.image,
            existingLogoUrl: d.logo || d.logo_url || null,
            technical_meeting_date: keYmd(d.technical_meeting_date),
            registration_deadline: keYmd(d.registration_deadline),
            kuota_atlet: d.kuota_atlet ?? "",
            whatsapp_group: d.whatsapp_group || "",
            start_date: keYmd(d.start_date),
            end_date: keYmd(d.end_date),
            status: normalkanStatus(d.status_normal || d.status),
            description: d.description || "",
            location: d.location || "",
            bank_name: d.bank_name || "",
            bank_account_number: d.bank_account_number || "",
            bank_account_name: d.bank_account_name || "",
            payment_note: d.payment_note || "",
            tampilkan_kuota_kelas: d.tampilkan_kuota_kelas !== undefined ? Boolean(d.tampilkan_kuota_kelas) : true,
          };
        }
      } catch (error) {
        this.fetchFailed = true;
        this.toast.error(error.friendlyMessage || "Gagal memuat data turnamen.");
      } finally {
        this.loading = false;
      }
    },

    
    async submitForm() {
      this.errors = {};
      this.loading = true;

      const formData = new FormData();

      // Pastikan format tanggal murni Y-m-d sebelum dimasukkan
      if (this.form.start_date) this.form.start_date = keYmd(this.form.start_date);
      if (this.form.end_date) this.form.end_date = keYmd(this.form.end_date);
      if (this.form.technical_meeting_date) this.form.technical_meeting_date = keYmd(this.form.technical_meeting_date);
      if (this.form.registration_deadline) this.form.registration_deadline = keYmd(this.form.registration_deadline);

      // Loop semua isi form non-berkas
      for (const key in this.form) {
        if (
          key === 'tournament_document' ||
          key === 'tournament_image' ||
          key === 'tournament_logo' ||
          key === 'existingDocumentUrl' ||
          key === 'existingImageUrl' ||
          key === 'existingLogoUrl'
        ) {
          continue;
        }
        if (this.form[key] !== null && this.form[key] !== undefined && this.form[key] !== "") {
          formData.append(key, this.form[key]);
        }
      }
      formData.set("tampilkan_kuota_kelas", this.form.tampilkan_kuota_kelas ? "1" : "0");
      // Selalu dikirim supaya batas bisa dihapus (kosong = tanpa batas).
      formData.set("kuota_atlet", this.form.kuota_atlet === null || this.form.kuota_atlet === undefined ? "" : String(this.form.kuota_atlet));

      if (this.form.tournament_document instanceof File) {
        formData.append("tournament_document", this.form.tournament_document);
      }

      if (this.form.tournament_image instanceof File) {
        formData.append("tournament_image", this.form.tournament_image);
      }

      if (this.form.tournament_logo instanceof File) {
        formData.append("tournament_logo", this.form.tournament_logo);
        formData.append("logo", this.form.tournament_logo);
      }

      if (!this.isEdit) {
        if (!this.form.tournament_document && !this.form.tournament_image) {
          this.toast.error("Silakan unggah proposal kejuaraan (PDF) atau banner gambar.");
          this.loading = false;
          return;
        }
      }

      try {
        const targetId = this.targetId;
        const endpoint = this.isEdit ? `/tournament-settings/update/${targetId}` : "/tournament-settings";

        await http.post(endpoint, formData);

        this.toast.success(this.isEdit ? "Turnamen berhasil diperbarui!" : "Turnamen berhasil ditambahkan!");
        this.$router.push("/admin/tournament");
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
          this.errors = error.response.data.errors;
          const msg = error.response.data.message || "Terdapat isian formulir yang belum sesuai.";
          this.toast.error(msg);
        } else {
          this.toast.error(error.friendlyMessage || "Gagal menyimpan turnamen. Silakan coba lagi.");
        }
      } finally {
        this.loading = false;
      }
    },

  },
};
</script>


<style scoped>
.rekening-judul {
  margin: 1.5rem 0 0.5rem;
  font-size: 1.05rem;
  font-weight: 700;
}

.rekening-catatan {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.7rem 0.85rem;
  margin-bottom: 1rem;
  border-radius: 8px;
  background: rgba(217, 119, 6, 0.1);
  color: #b45309;
  font-size: 0.9rem;
  line-height: 1.55;
}


.progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 5px;
  background: var(--grad-flame);
  animation: loader-animation 1.5s infinite;
}

.page-title {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 20px;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
}

/* Animasi garis loader */
@keyframes loader-animation {
  0% {
    width: 0;
    background: var(--grad-flame); /* Warna awal */
  }
  50% {
    width: 50%;
    background: var(--grad-flame); /* Warna saat animasi */
  }
  100% {
    width: 100%;
    background: var(--grad-flame);
  }
}
</style>
